/*
 * sdv-billetterie.js — ouvre la billetterie Weezevent dans une modale.
 *
 * Pourquoi : les liens billetterie envoyaient le visiteur sur un site externe.
 * Weezevent a déprécié les iframes posées à la main au profit d'un script
 * (widget.weezevent.com/weez.js) qui installe un MutationObserver sur le body :
 * il rend tout <a class="weezevent-widget-integration"> inséré APRÈS son
 * chargement. C'est ce qui permet d'injecter le widget à l'ouverture de la
 * modale, sans rechargement ni bidouille.
 *
 * Deux garde-fous volontaires :
 *   1. le script tiers n'est chargé qu'à la première ouverture — donc aucun
 *      cookie Weezevent n'est déposé sur salonduvintage.com tant que le
 *      visiteur n'a pas demandé à voir les billets. Seule exception, voulue :
 *      les pages salon, où la billetterie est aussi affichée dans la page —
 *      elle s'y charge à l'approche du bloc, sans clic ;
 *   2. si le widget ne se rend pas (script bloqué, panne Weezevent), la modale
 *      affiche le lien direct : on ne remplace jamais un lien qui marche par
 *      une zone vide.
 *
 * Interrupteur : réglage de thème « Billetterie dans une modale » (Personnaliser
 * → SDV — Identité). Décoché, tous les liens repartent en nouvel onglet.
 *
 * Billetterie intégrée : tout élément [data-billetterie-integree] reçoit le
 * même widget, directement dans la page. Elle a son propre réglage (« Afficher
 * la billetterie dans les pages salon ») et ne dépend pas de la modale.
 */
(function () {
  "use strict";

  var script = document.currentScript;
  // Ce réglage ne coupe que l'ouverture en fenêtre : la billetterie intégrée
  // aux pages salon a son propre interrupteur, côté Liquid.
  var modaleActive = !(script && script.dataset.modale === "false");

  var HOTE_WIDGET = "widget.weezevent.com";
  var SRC_WEEZ = "https://widget.weezevent.com/weez.js";
  var DELAI_REPLI_MS = 8000;

  var weezDemande = false;
  var modale = null;
  var conteneur = null;
  var replis = null;
  var minuteurRepli = null;
  var declencheur = null;

  function traduire(fr, en, es) {
    var lang = (document.documentElement.getAttribute("lang") || "fr").slice(0, 2).toLowerCase();
    if (lang === "en") return en;
    if (lang === "es") return es;
    return fr;
  }

  /** Locale Weezevent correspondant à la langue de la page. */
  function localeWeezevent() {
    var lang = (document.documentElement.getAttribute("lang") || "fr").slice(0, 2).toLowerCase();
    if (lang === "en") return "en-GB";
    if (lang === "es") return "es-ES";
    return "fr-FR";
  }

  function estLienBilletterie(a) {
    if (!a || !a.href) return false;
    try {
      return new URL(a.href, window.location.origin).hostname === HOTE_WIDGET;
    } catch (e) {
      return false;
    }
  }

  /** Construit le data-src attendu par weez.js à partir de l'URL du widget. */
  function sourceWidget(href) {
    var url = new URL(href, window.location.origin);
    url.searchParams.set("locale", localeWeezevent());
    url.searchParams.set("width_auto", "1");
    if (!url.searchParams.get("color_primary")) url.searchParams.set("color_primary", "000000");
    return url.toString();
  }

  function identifiantEvenement(href) {
    try {
      return new URL(href, window.location.origin).searchParams.get("id_evenement") || "";
    } catch (e) {
      return "";
    }
  }

  /** Lien-support que weez.js remplace par l'iframe du widget. */
  function creerLienWidget(href) {
    var lien = document.createElement("a");
    lien.className = "weezevent-widget-integration";
    lien.title = "Logiciel billetterie en ligne";
    lien.href = href;
    lien.target = "_blank";
    lien.rel = "noopener";
    lien.textContent = "Billetterie Weezevent";
    lien.setAttribute("data-src", sourceWidget(href));
    lien.setAttribute("data-width", "650");
    lien.setAttribute("data-height", "600");
    lien.setAttribute("data-resize", "1");
    lien.setAttribute("data-nopb", "0");
    lien.setAttribute("data-type", "neo");
    lien.setAttribute("data-width_auto", "1");
    lien.setAttribute("data-noscroll", "0");
    var idEvenement = identifiantEvenement(href);
    if (idEvenement) lien.setAttribute("data-id", idEvenement);
    return lien;
  }

  function texteRepli(href) {
    return (
      traduire(
        "La billetterie ne s'affiche pas ici. ",
        "The ticket shop can't be displayed here. ",
        "La venta de entradas no se puede mostrar aquí. "
      ) +
      '<a href="' + href + '" target="_blank" rel="noopener">' +
      traduire("Ouvrir la billetterie", "Open the ticket shop", "Abrir la venta de entradas") +
      " ↗︎</a>"
    );
  }

  function construireModale() {
    if (modale) return modale;

    modale = document.createElement("div");
    modale.className = "sdv-billetterie-modal";
    modale.setAttribute("hidden", "");
    modale.innerHTML =
      '<div class="sdv-billetterie-modal__backdrop" data-fermer></div>' +
      '<div class="sdv-billetterie-modal__panel" role="dialog" aria-modal="true" aria-label="' +
        traduire("Billetterie", "Tickets", "Entradas") + '">' +
        '<button type="button" class="sdv-billetterie-modal__close" data-fermer aria-label="' +
          traduire("Fermer", "Close", "Cerrar") + '">&#215;</button>' +
        '<div class="sdv-billetterie-modal__body" data-widget></div>' +
        '<p class="sdv-billetterie-modal__fallback" data-repli hidden></p>' +
      "</div>";

    document.body.appendChild(modale);
    conteneur = modale.querySelector("[data-widget]");
    replis = modale.querySelector("[data-repli]");

    modale.addEventListener("click", function (event) {
      if (event.target.closest("[data-fermer]")) fermer();
    });
    return modale;
  }

  function afficherRepli(href) {
    if (!replis) return;
    replis.innerHTML = texteRepli(href);
    replis.hidden = false;
  }

  /** Le widget est rendu quand weez.js a inséré son iframe dans le conteneur. */
  function surveillerRendu(href) {
    clearTimeout(minuteurRepli);
    minuteurRepli = setTimeout(function () {
      if (conteneur && !conteneur.querySelector("iframe")) afficherRepli(href);
    }, DELAI_REPLI_MS);
  }

  function chargerWeez() {
    if (weezDemande) return;
    weezDemande = true;
    var s = document.createElement("script");
    s.src = SRC_WEEZ;
    s.async = true;
    s.defer = true;
    s.onerror = function () {
      weezDemande = false;
      if (conteneur && modale && !modale.hasAttribute("hidden")) afficherRepli(conteneur.dataset.href || "#");
      zonesIntegrees().forEach(function (zone) {
        if (zone.dataset.rendu) afficherRepliIntegre(zone);
      });
    };
    document.head.appendChild(s);
  }

  function ouvrir(href) {
    construireModale();
    conteneur.dataset.href = href;
    conteneur.innerHTML = "";
    replis.hidden = true;

    conteneur.appendChild(creerLienWidget(href));

    modale.removeAttribute("hidden");
    document.documentElement.classList.add("sdv-billetterie-open");

    // weez.js observe le body : l'insertion ci-dessus suffit à déclencher le rendu.
    chargerWeez();
    surveillerRendu(href);

    var fermeture = modale.querySelector(".sdv-billetterie-modal__close");
    if (fermeture) fermeture.focus();
  }

  function fermer() {
    if (!modale || modale.hasAttribute("hidden")) return;
    clearTimeout(minuteurRepli);
    modale.setAttribute("hidden", "");
    document.documentElement.classList.remove("sdv-billetterie-open");
    if (conteneur) conteneur.innerHTML = "";
    if (declencheur && document.contains(declencheur)) declencheur.focus();
    declencheur = null;
  }

  // ── Billetterie intégrée à la page (pages salon) ────────────────────────

  function zonesIntegrees() {
    return Array.prototype.slice.call(document.querySelectorAll("[data-billetterie-integree]"));
  }

  function afficherRepliIntegre(zone) {
    var repli = zone.parentNode && zone.parentNode.querySelector("[data-billetterie-repli]");
    if (!repli || zone.querySelector("iframe")) return;
    repli.innerHTML = texteRepli(zone.dataset.href || "#");
    repli.hidden = false;
  }

  function rendreIntegree(zone) {
    if (zone.dataset.rendu) return;
    var href = zone.dataset.href;
    if (!href) return;
    zone.dataset.rendu = "1";
    zone.appendChild(creerLienWidget(href));
    // weez.js observe le body : s'il est déjà là, l'insertion suffit ; sinon
    // il rend le lien à son chargement.
    chargerWeez();
    setTimeout(function () { afficherRepliIntegre(zone); }, DELAI_REPLI_MS);
  }

  function initialiserIntegrees() {
    var zones = zonesIntegrees();
    if (!zones.length) return;
    if (!("IntersectionObserver" in window)) {
      zones.forEach(rendreIntegree);
      return;
    }
    // Chargé un peu avant d'entrer à l'écran : le widget est prêt quand le
    // visiteur arrive dessus, sans peser sur le haut de page.
    var observateur = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (entree) {
        if (!entree.isIntersecting) return;
        observateur.unobserve(entree.target);
        rendreIntegree(entree.target);
      });
    }, { rootMargin: "600px 0px" });
    zones.forEach(function (zone) { observateur.observe(zone); });
  }

  // ── Boutons « Acheter mes billets » des pages salon ─────────────────────

  // Défilement fluide jusqu'à la billetterie : le saut brutal faisait perdre
  // au visiteur le fil de la page.
  document.addEventListener("click", function (event) {
    var lien = event.target.closest('a[href="#billetterie"]');
    if (!lien) return;
    var cible = document.getElementById("billetterie");
    if (!cible) return;
    event.preventDefault();
    var reduit = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    cible.scrollIntoView({ behavior: reduit ? "auto" : "smooth", block: "start" });
    if (window.history && window.history.replaceState) window.history.replaceState(null, "", "#billetterie");
  });

  // Barre mobile : visible seulement quand aucun autre bouton billets ni la
  // billetterie elle-même ne sont à l'écran.
  function initialiserBarreMobile() {
    var barre = document.querySelector("[data-salon-cta-mobile]");
    if (!barre) return;
    var temoins = [document.querySelector("[data-salon-cta]"), document.getElementById("billetterie")].filter(Boolean);
    if (!("IntersectionObserver" in window) || !temoins.length) {
      barre.classList.add("is-visible");
      return;
    }
    var visibles = new Set();
    var observateur = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (entree) {
        if (entree.isIntersecting) visibles.add(entree.target);
        else visibles.delete(entree.target);
      });
      barre.classList.toggle("is-visible", visibles.size === 0);
    });
    temoins.forEach(function (t) { observateur.observe(t); });
  }

  function initialiserPage() {
    initialiserIntegrees();
    initialiserBarreMobile();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialiserPage);
  } else {
    initialiserPage();
  }

  // ── Ouverture en fenêtre ────────────────────────────────────────────────

  // Délégation : couvre aussi les lignes d'agenda insérées en JS.
  document.addEventListener("click", function (event) {
    if (!modaleActive) return;
    var a = event.target.closest("a");
    if (!estLienBilletterie(a)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    declencheur = a;
    ouvrir(a.href);
  });

  document.addEventListener("keydown", function (event) {
    if (modaleActive && event.key === "Escape") fermer();
  });
})();
